import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Container, Typography, Paper, Button, Stack } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { authService } from '../services/authService';

/**
 * Success Page
 * Displays success message after OAuth authentication
 * Reads username from URL query parameters
 * Fetches user info from backend to populate localStorage (no redirects)
 */
const SuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username');
  const authStatus = searchParams.get('auth');

  useEffect(() => {
    // If authentication was successful, fetch user info from backend
    // This populates localStorage so authService.isAuthenticated() works
    // NO REDIRECTS - user stays on success page
    if (authStatus === 'success') {
      const fetchUser = async () => {
        try {
          // Backend already authenticated and set session cookie
          // Just fetch user info to populate localStorage
          await authService.getCurrentUser();
        } catch (error) {
          // Even if fetch fails, don't redirect - backend already authenticated
          // User can still see success message and navigate manually
          console.log('Could not fetch user info, but OAuth was successful');
        }
      };
      fetchUser();
    }
    // No redirect logic - user stays on success page
  }, [authStatus]);

  const handleGoToDashboard = () => {
    navigate('/dashboard');
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 4, sm: 8 } }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: '0 8px 40px rgba(42, 49, 66, 0.1)',
          background: 'linear-gradient(165deg, rgba(255,255,255,0.98) 0%, rgba(255,255,255,0.92) 100%)',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          {authStatus === 'success' ? (
            <>
              <CheckCircleIcon 
                sx={{ 
                  fontSize: 80, 
                  color: 'success.main', 
                  mb: 2 
                }} 
              />
              <Typography variant="h4" component="h1" gutterBottom color="success.main">
                Login Successful!
              </Typography>
              {username && (
                <Typography variant="h5" component="h2" gutterBottom color="text.primary" sx={{ mt: 2 }}>
                  Welcome, {username}!
                </Typography>
              )}
              <Typography variant="body1" color="text.secondary" sx={{ mt: 3, mb: 4 }}>
                Your Reddit account has been successfully connected to SafeSocial.
                You can now start scanning your posts and comments for privacy exposures.
              </Typography>
              
              <Stack spacing={2} sx={{ mt: 4 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  onClick={handleGoToDashboard}
                  sx={{
                    py: 1.5,
                    textTransform: 'none',
                    fontSize: '1.1rem',
                  }}
                >
                  Go to Dashboard
                </Button>
                <Button
                  variant="outlined"
                  size="medium"
                  fullWidth
                  onClick={() => navigate('/exposures')}
                  sx={{
                    textTransform: 'none',
                  }}
                >
                  View Exposures
                </Button>
              </Stack>
            </>
          ) : (
            <>
              <Typography variant="h4" component="h1" gutterBottom color="text.primary">
                Authentication Status
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Processing authentication...
              </Typography>
            </>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default SuccessPage;
